import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, AlertTriangle, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const VALIDITY = [['30', '30 dni'], ['90', '90 dni'], ['365', '365 dni'], ['none', 'Bez limitu']];
const field = 'bg-[var(--crm-bg-main)] border-[var(--crm-border)] text-[var(--crm-text-primary)]';

const KeyReveal = ({ created, onClose }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(created.key);
    setCopied(true);
    toast.success('Klucz skopiowany');
  };
  return (
    <>
      <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 flex gap-2 text-sm text-amber-500" data-testid="api-key-save-warning">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        Zapisz go teraz – nie pokażemy go ponownie.
      </div>
      <div className="flex gap-2">
        <Input readOnly value={created.key} className={`${field} font-mono text-xs`} data-testid="api-key-full-value" onFocus={(e) => e.target.select()} />
        <Button onClick={copy} className="bg-[#FF6B35] hover:bg-[#e55a2a] text-white" data-testid="api-key-copy-btn">
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        </Button>
      </div>
      <DialogFooter>
        <Button onClick={onClose} variant="outline" data-testid="api-key-reveal-close-btn">Zapisałem klucz – zamknij</Button>
      </DialogFooter>
    </>
  );
};

export const NewApiKeyDialog = ({ open, onOpenChange, users, onCreated }) => {
  const [form, setForm] = useState({ name: '', user_id: '', scope: 'read', validity: '90' });
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const close = () => { setCreated(null); setForm({ name: '', user_id: '', scope: 'read', validity: '90' }); onOpenChange(false); };

  const submit = async () => {
    if (!form.name.trim() || !form.user_id) { toast.error('Podaj nazwę i wybierz konto'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api-keys`, {
        name: form.name.trim(), user_id: form.user_id, scope: form.scope,
        expires_in_days: form.validity === 'none' ? null : Number(form.validity),
      });
      setCreated(res.data);
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Nie udało się utworzyć klucza');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)] max-w-lg" data-testid="new-api-key-dialog">
        <DialogHeader>
          <DialogTitle>{created ? 'Klucz utworzony' : 'Nowy klucz API'}</DialogTitle>
          <DialogDescription className="text-[var(--crm-text-secondary)]">
            {created ? `Klucz „${created.name}” (${created.key_prefix}…)` : 'Klucz działa w imieniu wybranego konta – z dokładnie jego uprawnieniami.'}
          </DialogDescription>
        </DialogHeader>
        {created ? <KeyReveal created={created} onClose={close} /> : (
          <>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Nazwa</Label>
                <Input value={form.name} onChange={(e) => set('name')(e.target.value)} placeholder="np. Sauna AI" className={field} data-testid="api-key-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Konto</Label>
                <Select value={form.user_id} onValueChange={set('user_id')}>
                  <SelectTrigger className={field} data-testid="api-key-user-select"><SelectValue placeholder="Wybierz konto" /></SelectTrigger>
                  <SelectContent>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id} data-testid={`api-key-user-option-${u.id}`}>{u.initials} – {u.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Zakres</Label>
                  <Select value={form.scope} onValueChange={set('scope')}>
                    <SelectTrigger className={field} data-testid="api-key-scope-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="read" data-testid="api-key-scope-read">Tylko odczyt</SelectItem>
                      <SelectItem value="read_write" data-testid="api-key-scope-read-write">Odczyt i zapis</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Ważność</Label>
                  <Select value={form.validity} onValueChange={set('validity')}>
                    <SelectTrigger className={field} data-testid="api-key-validity-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {VALIDITY.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`api-key-validity-${v}`}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={close} data-testid="api-key-cancel-btn">Anuluj</Button>
              <Button onClick={submit} disabled={saving} className="bg-[#FF6B35] hover:bg-[#e55a2a] text-white" data-testid="api-key-create-submit-btn">
                {saving ? 'Tworzenie…' : 'Utwórz klucz'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
